import { RmsAccountSelection } from '@/ui/features/badging/badging-customizations/badge-create/types';
import { AccountBadgeOptionsSelectProps } from '@juvare/badging/ui/@types/AccountBadgeOptionsSelectProps';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { OccupationColumns, occupationEntityName } from '@juvare/credentialing/backend/config/shared';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { CircularProgress, MenuItem, Select } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';

export const RmsAccountOccupationSelect: React.FC<AccountBadgeOptionsSelectProps<RmsAccountSelection>> = ({
    accountSelection,
    onOptionsPartialChange,
    hideDropdownWhenNoOptions,
    hideDropdownWhenOneOption,
    sx,
}) => {
    const { t } = useTranslation('rms');

    const { data, isLoading } = useStorageList<OccupationColumns>(occupationEntityName, {}, undefined, undefined, {
        dedupingInterval: 30000, // avoid unnecessary re-fetching
    } satisfies SWRConfiguration);

    const occupations = data?.rows ?? [];
    const accountOccupations = occupations.filter((occupation) => accountSelection.account.occupationIds.includes(occupation.id));
    const sxWidth = (sx as { width?: string | number } | undefined)?.width;

    if (isLoading) {
        return <CircularProgress />;
    }
    if (accountOccupations.length === 0 && hideDropdownWhenNoOptions) {
        return (
            <Typography
                variant='body1'
                sx={{
                    textAlign: 'center',
                    width: sxWidth ?? '100%',
                }}
            >
                -
            </Typography>
        );
    }
    if (
        accountOccupations.length === 1 &&
        accountOccupations[0].id === accountSelection.options.occupationId &&
        hideDropdownWhenOneOption
    ) {
        return (
            <Typography
                variant='body1'
                sx={{
                    textAlign: 'center',
                    width: sxWidth ?? '100%',
                }}
            >
                {accountOccupations[0].columns.name}
            </Typography>
        );
    }

    return (
        <Select
            displayEmpty
            value={accountSelection.options.occupationId ?? ''}
            onChange={(event) => onOptionsPartialChange({ occupationId: event.target.value || '' })}
            disabled={
                accountOccupations.length == 0 ||
                (accountOccupations.length === 1 && accountOccupations[0].id == accountSelection.options.occupationId)
            }
            variant='outlined'
            sx={sx}
        >
            <MenuItem value='' disabled>
                <Typography variant='body1' sx={{ color: themeColors.text.disable }}>
                    {t('badging.accountBadgeTable.selectOccupation')}
                </Typography>
            </MenuItem>
            {accountOccupations.map((occupation) => (
                <MenuItem key={occupation.id} value={occupation.id}>
                    {occupation.columns.name}
                </MenuItem>
            ))}
        </Select>
    );
};
